'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface SplashScreenProps {
  children: React.ReactNode
}

export default function SplashScreen({ children }: SplashScreenProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [showVideo, setShowVideo] = useState(false) // Start with false for server/client match
  const [fadeClass, setFadeClass] = useState('opacity-0')
  const [appFadeClass, setAppFadeClass] = useState('opacity-0')
  const videoRef = useRef<HTMLVideoElement>(null)
  const timeoutRefs = useRef<(NodeJS.Timeout | null)[]>([])
  const animationFrameRef = useRef<number | null>(null)

  const cleanupScrollBlock = useCallback(() => {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
    document.body.style.height = ''
  }, [])

  const completeSplash = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      setShowVideo(false)
      setIsLoading(false)
      setAppFadeClass('opacity-100')
      cleanupScrollBlock()
    })
  }, [cleanupScrollBlock])

  const addTimeout = useCallback((callback: () => void, delay: number) => {
    const timeout = setTimeout(callback, delay)
    timeoutRefs.current.push(timeout)
    return timeout
  }, [])

  const clearAllTimeouts = useCallback(() => {
    for (const timeout of timeoutRefs.current) {
      if (timeout) clearTimeout(timeout)
    }
    timeoutRefs.current = []
  }, [])

  useEffect(() => {
    // Only run on client side after hydration
    if (typeof window === 'undefined') return

    const initializeSplash = () => {
      // Check if splash has been shown in this session
      const hasShownSplash = sessionStorage.getItem('rtsc_splash_shown') === 'true'

      if (hasShownSplash || process.env.NEXT_PUBLIC_DISABLE_SPLASH === 'true') {
        completeSplash()
        return
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (prefersReducedMotion) {
        completeSplash()
        return
      }

      // Mark splash as shown for this session
      sessionStorage.setItem('rtsc_splash_shown', 'true')

      // Now show the splash
      setShowVideo(true)

      const originalStyles = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        width: document.body.style.width,
        height: document.body.style.height,
      }

      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'

      addTimeout(() => {
        animationFrameRef.current = requestAnimationFrame(() => {
          setFadeClass('opacity-100')
        })
      }, 200)

      addTimeout(() => {
        animationFrameRef.current = requestAnimationFrame(() => {
          setAppFadeClass('opacity-100')
        })

        addTimeout(() => {
          setFadeClass('opacity-0')
        }, 300)

        addTimeout(() => {
          completeSplash()
        }, 1000)
      }, 2500)
    }

    // Small delay to ensure this runs after hydration
    const timer = setTimeout(initializeSplash, 50)

    return () => {
      clearTimeout(timer)
      clearAllTimeouts()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      // Restore original styles
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
    }
  }, [addTimeout, clearAllTimeouts, completeSplash])

  const handleVideoError = useCallback(() => {
    console.warn('Splash video failed to load, skipping splash screen')
    clearAllTimeouts()
    completeSplash()
  }, [clearAllTimeouts, completeSplash])

  const handleVideoLoaded = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    video.playbackRate = 1
    video.volume = 0

    video.play().catch((error) => {
      console.warn('Video autoplay blocked:', error)
    })
  }, [])

  // Always render the same structure - server and client match
  return (
    <>
      {showVideo && (
        <dialog
          open
          className='fixed inset-0 w-screen h-screen z-50 flex items-center justify-center bg-gradient-to-b from-[#FCFCFF] to-white border-0 p-0 m-0 max-w-none max-h-none outline-0'
        >
          <div
            className={`transition-opacity duration-1000 ${fadeClass}`}
            style={{
              transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
              willChange: 'opacity',
            }}
          >
            <video
              ref={videoRef}
              className='max-w-full max-h-full object-contain will-change-transform'
              style={{
                maxWidth: '40vw',
                maxHeight: '40vh',
                transform: 'translateZ(0)',
              }}
              autoPlay
              muted
              playsInline
              preload='auto'
              onLoadedData={handleVideoLoaded}
              onError={handleVideoError}
              onCanPlayThrough={handleVideoLoaded}
              controlsList='nodownload nofullscreen noremoteplayback'
              disablePictureInPicture
              disableRemotePlayback
              poster=''
              crossOrigin='anonymous'
            >
              <source src='/animations/intro.webm' type='video/webm' />
              <source src='/animations/intro.mp4' type='video/mp4' />
              <div className='flex items-center justify-center text-gray-600 p-4'>
                <div className='text-center'>
                  <div className='mb-2 text-sm'>Loading...</div>
                  <div className='w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto' />
                </div>
              </div>
            </video>
          </div>
        </dialog>
      )}

      <div
        className={`transition-opacity duration-1000 ${showVideo ? 'opacity-0' : appFadeClass}`}
        style={{
          contain: 'layout style paint',
          transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'opacity',
        }}
      >
        {children}
      </div>
    </>
  )
}
